import { useState } from 'react';
import { useUppyEvent } from '@uppy/react';
import { createVirtualBackgroundUppyInstance } from 'features/virtual-backgrounds/utils/createVirtualBackgroundUppyInstance';
import { isError, isUppyRestrictionError } from 'utils/types';
import { UppyFile } from '@uppy/core';
import * as Sentry from '@sentry/react';
import { logger } from 'utils/logger';
import { useTranslation } from 'react-i18next';

interface UseCustomBackgroundUploadOptions {
  onUpload: (file: UppyFile<any, any>) => void;
}

export const useVirtualBackgroundUpload = ({ onUpload }: UseCustomBackgroundUploadOptions) => {
  const { t } = useTranslation('settings');

  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [uppy] = useState(() => createVirtualBackgroundUppyInstance());

  useUppyEvent(uppy, 'file-added', (file) => {
    onUpload(file);

    uppy.clear();
  });

  useUppyEvent(uppy, 'error', (err) => {
    setIsUploading(false);

    setError(t('virtual_bg.uploader_errors.error'));

    Sentry.captureException(err);
    logger.remote().error(`Cannot upload a virtual background`, err);
  });

  const handleFileSelect = (file: File) => {
    setIsUploading(true);
    setError(undefined);

    try {
      uppy.addFile({
        name: file.name,
        type: file.type,
        data: file,
      });
    } catch (err) {
      if (isUppyRestrictionError(err)) {
        setError(err.message);
      } else {
        if (isError(err)) {
          setError(t('virtual_bg.bg_upload_error', { fileName: file.name }));
        } else {
          setError(t('virtual_bg.bg_upload_general_error'));
        }

        Sentry.captureException(err);
        logger.remote().error(`Cannot upload a virtual background=${file.name}`, err);
      }
    } finally {
      setIsUploading(false);
    }
  };

  return {
    handleFileSelect,
    isUploading,
    error,
  };
};
